// src/components/SchoolUsers/PasswordReset/PasswordResetModal.js
import React, { useState } from "react";
import {
  DialogContent,
  TextField,
  Alert,
  Typography,
  Box,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import { updateUserPassword } from "../../../config/firebase";
import { getAuth } from "firebase/auth";
import ModalDialogTitle from "../../common/ModalDialogTitle";
import ModalActions from "../../common/ModalActions";

const PasswordResetModal = ({ user, onSuccess, onClose }) => {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    // Validation
    if (!newPassword || !confirmPassword) {
      setError("Please fill in both password fields");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters long");
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Prefer calling a deployed HTTP function if configured (onRequest). This
      // avoids httpsCallable vs onRequest mismatches when functions are deployed
      // as HTTP endpoints. Set REACT_APP_UPDATE_USER_PASSWORD_URL to the full
      // function URL (e.g. https://us-central1-<proj>.cloudfunctions.net/updateUserPassword)
      const functionUrl = process.env.REACT_APP_UPDATE_USER_PASSWORD_URL;

      console.log("PasswordResetModal: configured functionUrl=", functionUrl);

      if (functionUrl) {
        // Include ID token if user is authenticated
        const auth = getAuth();
        let idToken = null;
        if (auth && auth.currentUser) {
          try {
            idToken = await auth.currentUser.getIdToken();
          } catch (tokErr) {
            console.warn("Could not get ID token:", tokErr);
          }
        }

        const headers = {
          "Content-Type": "application/json",
          ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
        };

        let resp;
        try {
          resp = await fetch(functionUrl, {
            method: "POST",
            headers,
            body: JSON.stringify({ uid: user.id, newPassword }),
          });
        } catch (fetchErr) {
          console.error(
            "Network error when calling updateUserPassword:",
            fetchErr,
          );
          setError(
            "Network error: could not reach password update service. Check your network, function URL, and browser console.",
          );
          throw fetchErr;
        }

        console.log("PasswordResetModal: response status=", resp.status);

        let data = {};
        try {
          const text = await resp.text();
          try {
            data = JSON.parse(text || "{}");
          } catch (e) {
            data = { raw: text };
          }
        } catch (e) {
          console.warn("Could not read response body", e);
        }

        if (!resp.ok) {
          console.error(
            "updateUserPassword returned non-ok",
            resp.status,
            data,
          );
          const msg =
            data.message || data.error || data.raw || `HTTP ${resp.status}`;
          setError(`Failed to update password: ${msg}`);
          return;
        }

        // Accept multiple possible shapes from server (success boolean or status)
        if (data.success || data.status === true) {
          onSuccess();
        } else {
          const msg =
            data.message ||
            data.error ||
            data.raw ||
            "Failed to update password";
          setError(msg);
        }
      } else {
        // Fallback to existing httpsCallable wrapper when no HTTP URL configured
        const result = await updateUserPassword(user.id, newPassword);

        if (result.success) {
          onSuccess();
        } else {
          setError(result.message || "Failed to update password");
        }
      }
    } catch (error) {
      console.error("Error updating password:", error);
      setError(
        error.message ||
          "An unexpected error occurred while updating the password",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <ModalDialogTitle onClose={onClose}>
        <Box display="flex" alignItems="center" gap={1}>
          <LockIcon />
          Reset Password
        </Box>
      </ModalDialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Resetting password for: <strong>{user.name}</strong> ({user.email})
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          autoFocus
          margin="dense"
          label="New Password"
          type="password"
          fullWidth
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          helperText="Password must be at least 6 characters long"
        />

        <TextField
          margin="dense"
          label="Confirm New Password"
          type="password"
          fullWidth
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          helperText="Re-enter the new password to confirm"
        />
      </DialogContent>
      <ModalActions
        onSave={handleSubmit}
        saving={loading}
        saveLabel="Update Password"
      />
    </>
  );
};

export default PasswordResetModal;
